<template>
  <div class="inno-mobile-search-bar">
    <div :class="{ 'inno-mobile-search-bar__backdrop': searchBarVisible }"></div>

    <transition name="slide-down">
      <div ref="contentElement" class="inno-mobile-search-bar__content">
        <div ref="wrapperElement" class="inno-mobile-search-bar__wrapper">
          <VcInput
            v-model="searchPhrase"
            type="search"
            :maxlength="maxSearchLength"
            :placeholder="$t('shared.layout.header.mobile.search_bar.input_placeholder')"
            class="inno-mobile-search-bar__input"
            :clearable="!!searchPhrase"
            @clear="reset"
            @input="showSearchBar()"
            @keyup.enter="goToSearchResultsPage"
          >
            <template #append>
              <VcIcon
                :aria-label="$t('shared.layout.search_bar.search_button')"
                name="custom-search"
                class="mr-2"
                :loading="loading"
                @click="goToSearchResultsPage()"
              />
            </template>
          </VcInput>
        </div>

        <VcScrollbar class="inno-mobile-search-bar__dropdown" :vertical="isMobile">
          <InnoSearchDropdown
            ref="searchDropdownRef"
            :visible="searchBarVisible"
            :search-phrase="searchPhrase"
            :filter-expression="filterExpression"
            :categories-filter-expression="categoriesFilterExpression"
            @hide="hideSearchBar"
            @product-select="hideSearchBar"
            @update:search-phrase="searchPhrase = $event"
          />
        </VcScrollbar>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import type { RouteLocationRaw } from "vue-router";
import { useElementBounding, useBreakpoints, useLocalStorage } from "@vueuse/core";
import { computed, onMounted, ref } from "vue";
import { useRouteQueryParam, useThemeContext } from "@/core/composables";
import { useModuleSettings } from "@/core/composables/useModuleSettings";
import { BREAKPOINTS, IN_STOCK_PRODUCTS_LOCAL_STORAGE } from "@/core/constants";
import { MODULE_XAPI_KEYS } from "@/core/constants/modules";
import { QueryParamName } from "@/core/enums";
import { globals } from "@/core/globals";
import {
  getFilterExpressionForCategorySubtree,
  getFilterExpressionForInStockVariations,
  getFilterExpressionForZeroPrice,
} from "@/core/utilities";
import { useSearchBar } from "@/shared/layout/composables/useSearchBar";
import InnoSearchDropdown from "./inno-search-dropdown.vue";
import { useHistoricalEvents } from "@/core/composables/useHistoricalEvents";
import { useTweakwise } from "@/shared/tweakwise";

interface IProps {
  visible: boolean;
}

defineProps<IProps>();

const searchPhrase = ref("");
const searchPhraseInUrl = useRouteQueryParam<string>(QueryParamName.SearchPhrase);

const { hideSearchBar, maxSearchLength, loading, showSearchBar, searchBarVisible } = useSearchBar();
const { saveSearchQuery, useGetSearchHistoryQuery } = useHistoricalEvents();

const { themeContext } = useThemeContext();
const { getSettingValue } = useModuleSettings(MODULE_XAPI_KEYS.MODULE_ID);
const { tweakwiseEvent } = useTweakwise();

const breakpoints = useBreakpoints(BREAKPOINTS);
const isMobile = breakpoints.smaller("md");
const router = useRouter();

function getSearchRoute(phrase: string): RouteLocationRaw {
  return {
    name: "Search",
    query: {
      [QueryParamName.SearchPhrase]: phrase,
    },
  };
}

function goToSearchResultsPage() {
  if (searchPhrase.value) {
    hideSearchBar();
    saveSearchQuery(searchPhrase.value);

    tweakwiseEvent("search", {
      searchTerm: searchPhrase.value,
    });

    void router.push(getSearchRoute(searchPhrase.value));

    searchPhrase.value = "";
  }
}

const localStorageInStock = useLocalStorage<boolean>(IN_STOCK_PRODUCTS_LOCAL_STORAGE, true);

const filterExpression = computed(() => {
  const scopeExpression = getFilterExpressionForCategorySubtree({ catalogId: globals.catalogId });
  const { zero_price_product_enabled } = themeContext.value.settings;
  const catalog_empty_categories_enabled = getSettingValue(MODULE_XAPI_KEYS.CATALOG_EMPTY_CATEGORIES_ENABLED);

  if (catalog_empty_categories_enabled) {
    return undefined;
  }

  const expressions = [
    scopeExpression,
    getFilterExpressionForZeroPrice(!!zero_price_product_enabled, globals.currencyCode),
    getFilterExpressionForInStockVariations(localStorageInStock.value),
  ].filter(Boolean);

  return expressions.length > 0 ? expressions.join(" ") : undefined;
});

const categoriesFilterExpression = computed(() => {
  const catalog_empty_categories_enabled = getSettingValue(MODULE_XAPI_KEYS.CATALOG_EMPTY_CATEGORIES_ENABLED);

  if (catalog_empty_categories_enabled) {
    return undefined;
  }

  return getFilterExpressionForCategorySubtree({ catalogId: globals.catalogId });
});
const contentElement = ref<HTMLElement | null>(null);
const wrapperElement = ref<HTMLElement | null>(null);

const { top: contentTop } = useElementBounding(contentElement);
const { height: wrapperHeight } = useElementBounding(wrapperElement);

const dropdownMaxHeight = computed(() => {
  const dropdownTop = contentTop.value + wrapperHeight.value;
  return dropdownTop > 0 ? `calc(100dvh - ${dropdownTop}px)` : "auto";
});

function reset() {
  searchPhrase.value = "";

  void hideSearchBar();
}

onMounted(() => {
  if (searchPhraseInUrl.value) {
    searchPhrase.value = searchPhraseInUrl.value;
  }
});
</script>

<style lang="scss">
.inno-mobile-search-bar {
  &__backdrop {
    @apply fixed inset-0 z-10 cursor-pointer;

    background-color: rgba(194, 195, 195, 0.6);

    @media (min-width: theme("screens.lg")) {
      @apply hidden;
    }
  }

  &__content {
    @apply absolute left-0 right-0 z-10 bg-white;
  }

  &__wrapper {
    @apply flex select-none items-center p-3.5;
  }

  &__input {
    @apply grow;

    .vc-input__container {
      @apply bg-[--mobile-search-bar-bg];
      border-radius: 100px;
    }

    .vc-input__input::placeholder {
      @apply text-[--body-text-color];
    }
  }

  &__close {
    @apply appearance-none text-[--link-color] text-sm;
  }

  &__dropdown {
    max-height: v-bind(dropdownMaxHeight);
  }

  // Transitions styles
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .slide-down-enter-active {
    transition: transform 0.3s ease;
    transition-delay: 0.1s;
  }

  .slide-down-leave-active {
    transition: transform 0.3s ease;
  }

  .slide-down-enter-from {
    transform: translateY(-100%);
  }

  .slide-down-leave-to {
    transform: translateY(-100%);
  }
}
</style>
